import React from 'react';

import { Group, Paper, Stack, Text } from '@mantine/core';
import { useLocalization } from 'context/localization-context';
import { DiscountInvoiceItem, InvoiceItemWrite, InvoiceRetrieve } from 'types';

interface InvoiceItem extends InvoiceItemWrite {
  productName: string;
  discountValue?: number;
  discountType?: 'PERCENTAGE' | 'FIXED_AMOUNT';
  discountAmount?: number;
  discountInfo?: DiscountInvoiceItem;
}

interface InvoiceSummaryProps {
  athleteName: string | undefined;
  invoiceItems: InvoiceItem[];
  isViewing?: boolean;
  invoice?: InvoiceRetrieve;
}

const InvoiceSummary = ({
  athleteName,
  invoiceItems,
  invoice,
  isViewing,
}: InvoiceSummaryProps): JSX.Element => {
  const { formatCurrency, copy, calculateInvoiceSummary, isTaxInclusive } = useLocalization();

  const { total, subtotal, taxAmount, discountAmount } =
    isViewing && invoice
      ? {
          total: (invoice?.total ?? 0) / 100,
          subtotal: (invoice?.pretaxTotal ?? 0) / 100,
          taxAmount: (invoice?.tax ?? 0) / 100,
          discountAmount: invoiceItems.reduce((sum, iv) => sum + (iv.discountAmount ?? 0), 0),
        }
      : calculateInvoiceSummary(invoiceItems);

  const isDiscounted = discountAmount > 0;
  const showSubtotalStrikethrough = isDiscounted && !isTaxInclusive;
  const showTotalStrikethrough = isDiscounted && isTaxInclusive;
  const originalSubtotal = subtotal + discountAmount;
  const originalTotal = total + discountAmount;

  return (
    <Paper withBorder shadow='sm' p='md' mt='lg'>
      <Stack spacing='xs'>
        <Group position='apart' noWrap>
          <Text color='dimmed'>Athlete Name</Text>
          {/* TODO: wwe are accepting conditional invoice prop so athleteName is no longer needed, remove in invoice edit pr*/}
          <Text>{athleteName}</Text>
        </Group>
        <Group position='apart' noWrap>
          <Text color='dimmed'>Items</Text>
          <Text>{invoiceItems.map(iv => iv.productName).join(' + ')}</Text>
        </Group>
        <Group position='apart' noWrap align='start'>
          <Text color='dimmed'>{copy.financial.subtotal}</Text>
          <Stack spacing={0} align='flex-end'>
            {showSubtotalStrikethrough && (
              <Text strikethrough color='dimmed' size='sm'>
                {formatCurrency(originalSubtotal)}
              </Text>
            )}
            <Text color={showSubtotalStrikethrough ? 'green' : undefined}>{formatCurrency(subtotal)}</Text>
          </Stack>
        </Group>
        <Group position='apart' noWrap>
          <Text color='dimmed'>{copy.financial.tax}</Text>
          <Text>{formatCurrency(taxAmount)}</Text>
        </Group>
        <Group position='apart' noWrap align='start'>
          <Text color='dimmed' fw={700}>
            Total
          </Text>
          <Stack spacing={0} align='flex-end'>
            {showTotalStrikethrough && (
              <Text strikethrough color='dimmed' size='sm' fw={500}>
                {formatCurrency(originalTotal)}
              </Text>
            )}
            <Text fw={700} color={showTotalStrikethrough ? 'green' : undefined}>
              {formatCurrency(total)}
            </Text>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
};

export default InvoiceSummary;
